import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { CalendarSync, Copy, Check, ChevronDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function CalendarSyncCard({ compact = false, variant = "student" }) {
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(!compact);
  const [help, setHelp] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/calendar/sync/me").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.webcal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("error_generic"));
    }
  };

  const regenerate = async () => {
    if (!window.confirm(t("cal_regenerate_confirm"))) return;
    const r = await api.post("/calendar/sync/me/regenerate");
    setInfo(r.data);
    toast.success(t("cal_regenerated"));
  };

  if (!info) return null;
  const pill = "inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border border-slate-300 bg-white text-slate-800 hover:border-brand-blue hover:text-brand-blue transition-colors min-h-[40px]";

  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4 sm:p-5" data-testid="calendar-sync-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-start justify-between gap-3 text-left"
        data-testid="calendar-sync-toggle">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-white border border-sky-200 flex items-center justify-center shrink-0">
            <CalendarSync className="h-4 w-4 text-brand-blue" />
          </div>
          <div>
            <p className="font-display font-semibold text-slate-900">{t("cal_sync_title")}</p>
            <p className="text-xs text-slate-600 mt-0.5">{t(variant === "teacher" ? "cal_sync_sub_teacher" : "cal_sync_sub")}</p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 mt-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <a href={info.google_url} target="_blank" rel="noreferrer" className={pill} data-testid="cal-sync-google">Google Calendar</a>
            <a href={info.apple_url} className={pill} data-testid="cal-sync-apple">Apple Calendar</a>
            <a href={info.outlook_url} target="_blank" rel="noreferrer" className={pill} data-testid="cal-sync-outlook">Outlook</a>
            <button type="button" onClick={copy} className={pill} data-testid="cal-sync-copy">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? t("copied") : t("cal_copy_link")}
            </button>
          </div>
          <p className="text-xs text-slate-600">{t("cal_sync_note")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setHelp((h) => !h)} className="text-xs font-semibold text-brand-blue hover:underline"
              data-testid="cal-sync-help-toggle">
              {t("cal_how_it_works")}
            </button>
            <Button variant="ghost" size="sm" onClick={regenerate} className="h-7 text-xs text-slate-500" data-testid="cal-sync-regenerate">
              <RefreshCw className="h-3 w-3 mr-1" /> {t("cal_regenerate")}
            </Button>
          </div>
          {help && (
            <ol className="text-xs text-slate-700 space-y-1.5 list-decimal pl-5 bg-white rounded-xl border border-sky-100 p-3" data-testid="cal-sync-help">
              <li>{t("cal_help_1")}</li>
              <li>{t("cal_help_2")}</li>
              <li>{t("cal_help_3")}</li>
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
